import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import { uiStore } from '@/stores/ui.store';

import { highlightJson } from './json-highlighter';

export interface JsonViewerProps {
  data: unknown;
  id?: string;
  className?: string;
  maxHeight?: 'sm' | 'md' | 'lg' | 'xl' | 'none';
}

const MAX_HEIGHT_CLASSES = {
  sm: 'max-h-48',
  md: 'max-h-64',
  lg: 'max-h-96',
  xl: 'max-h-[32rem]',
  none: '',
} as const;

export const JsonViewer = observer(function JsonViewer({
  data,
  id,
  className = '',
  maxHeight = 'md',
}: JsonViewerProps) {
  const theme = uiStore.theme;
  const formatted = useMemo(() => JSON.stringify(data, null, 2), [data]);
  const [error, setError] = useState<string | null>(null);

  const [cachedHighlights, setCachedHighlights] = useState<{
    light: React.ReactElement[];
    dark: React.ReactElement[];
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    Promise.all([
      highlightJson(formatted, 'github-light'),
      highlightJson(formatted, 'github-dark'),
    ])
      .then(([light, dark]) => {
        if (!cancelled) {
          setCachedHighlights({ light, dark });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to highlight JSON:', err);
          setError('Syntax highlighting failed');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [formatted]);

  const displayElements = cachedHighlights?.[theme] ?? [];
  const maxHeightClass = MAX_HEIGHT_CLASSES[maxHeight];

  if (error) {
    return (
      <pre
        className={`text-xs whitespace-pre-wrap font-mono bg-surface p-3 rounded border border-border ${maxHeightClass} overflow-auto ${className}`}
        id={id}
      >
        {formatted}
      </pre>
    );
  }

  return (
    <pre
      className={`text-xs whitespace-pre-wrap font-mono bg-surface p-3 rounded border border-border ${maxHeightClass} overflow-auto ${className}`}
      id={id}
      aria-label={id ? `JSON content for ${id}` : 'JSON content'}
    >
      {displayElements.length > 0 ? displayElements : formatted}
    </pre>
  );
});

JsonViewer.displayName = 'JsonViewer';
